const SCALE_ALIASES = {
  "1min": "1m",
  "2min": "2m",
  "3min": "3m",
  "5min": "5m",
  "1day": "1d",
};

const CHART_TIME_ZONE = "America/Los_Angeles";
const US_EQUITY_MARKET_TIME_ZONE = "America/New_York";

const SCALE_TO_SECONDS = {
  "15s": 15,
  "30s": 30,
};

const SCALE_TO_MINUTES = {
  "1m": 1,
  "2m": 2,
  "3m": 3,
  "5m": 5,
  "10m": 10,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "2h": 120,
  "3h": 180,
  "4h": 240,
};

const DAILY_LIKE_SCALES = new Set(["1d", "1w", "1mo"]);
const US_EQUITY_LIKE_ASSET_TYPES = new Set(["stocks", "indices", "funds"]);
const EXTENDED_HOURS_QUOTE_ASSET_TYPES = new Set(["stocks", "funds"]);
const US_EQUITY_PREMARKET_START_MINUTES = 4 * 60;
const US_EQUITY_REGULAR_OPEN_MINUTES = 9 * 60 + 30;
const US_EQUITY_REGULAR_CLOSE_MINUTES = 16 * 60;
const US_EQUITY_EARLY_CLOSE_MINUTES = 13 * 60;
const US_EQUITY_OVERNIGHT_START_MINUTES = 20 * 60;
const TIME_ZONE_ABBREVIATION_OFFSETS = {
  UTC: "+00:00",
  GMT: "+00:00",
  EST: "-05:00",
  EDT: "-04:00",
  CST: "-06:00",
  CDT: "-05:00",
  MST: "-07:00",
  MDT: "-06:00",
  PST: "-08:00",
  PDT: "-07:00",
};

const pad2 = (value) => String(value).padStart(2, "0");

const zonedPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: CHART_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export const normalizeLiveChartScale = (scale) =>
  SCALE_ALIASES[String(scale || "").trim()] || String(scale || "").trim() || "1d";

const parseUtcDate = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const naiveMatch = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (naiveMatch) {
    const [, year, month, day, hour, minute, second] = naiveMatch;
    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second || "0"),
        0
      )
    );
  }

  const absolute = new Date(value);
  if (Number.isNaN(absolute.getTime())) return null;

  const parts = zonedPartsFormatter.formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const second = Number(get("second"));

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second, 0));
};

const parseAbsoluteDate = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const displayTimeMatch = value.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s+([A-Z]{2,4})$/i
  );
  if (displayTimeMatch) {
    const [, year, month, day, rawHour, minute, second, meridiem, zone] =
      displayTimeMatch;
    const offset = TIME_ZONE_ABBREVIATION_OFFSETS[zone.toUpperCase()];
    if (offset) {
      let hour = Number(rawHour);
      if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;
      const date = new Date(
        `${year}-${month}-${day}T${pad2(hour)}:${minute}:${second || "00"}${offset}`
      );
      if (!Number.isNaN(date.getTime())) return date;
    }
  }

  const absolute = new Date(value);
  return Number.isNaN(absolute.getTime()) ? null : absolute;
};

const hasExplicitTimeZone = (value) =>
  typeof value === "string" &&
  /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

const pacificOffsetFor = (rawDate) => {
  const absolute = parseAbsoluteDate(rawDate);
  if (!absolute) return null;

  const parts = zonedPartsFormatter.formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const second = Number(get("second"));
  if ([year, month, day, hour, minute, second].some((value) => !Number.isFinite(value))) {
    return null;
  }

  const wallClockAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsetMinutes = Math.round((wallClockAsUtc - absolute.getTime()) / 60_000);
  const absoluteMinutes = Math.abs(offsetMinutes);
  const sign = offsetMinutes >= 0 ? "+" : "-";
  return `${sign}${pad2(Math.floor(absoluteMinutes / 60))}:${pad2(absoluteMinutes % 60)}`;
};

const formatUtcDatetime = (date) =>
  `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;

// The chart's logical clock is Pacific wall time. Keep the real Pacific offset
// when a websocket payload has one, so presentation code can distinguish a
// regular-session live bar from a UTC timestamp with the same wall-clock text.
const formatChartDatetime = (date, sourceDatetime = null) => {
  const wallClock = formatUtcDatetime(date);
  if (!hasExplicitTimeZone(sourceDatetime)) return wallClock;

  const offset = pacificOffsetFor(sourceDatetime);
  return offset ? `${wallClock.replace(" ", "T")}${offset}` : wallClock;
};

const usEquityPartsFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: US_EQUITY_MARKET_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const usMarketHolidayCache = new Map();
const usMarketEarlyCloseCache = new Map();

const dateKey = (year, month, day) =>
  `${year}-${pad2(month)}-${pad2(day)}`;

const utcDate = (year, month, day) =>
  new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));

const isWeekendDate = (year, month, day) => {
  const weekday = utcDate(year, month, day).getUTCDay();
  return weekday === 0 || weekday === 6;
};

const observedNewYearsDay = (year) => {
  const date = utcDate(year, 1, 1);
  const weekday = date.getUTCDay();
  if (weekday === 0) return dateKey(year, 1, 2);
  if (weekday === 6) return null;
  return dateKey(year, 1, 1);
};

const observedFixedHoliday = (year, month, day) => {
  const date = utcDate(year, month, day);
  const weekday = date.getUTCDay();
  if (weekday === 6) {
    const observed = new Date(date.getTime() - 24 * 60 * 60 * 1000);
    return dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate());
  }
  if (weekday === 0) {
    const observed = new Date(date.getTime() + 24 * 60 * 60 * 1000);
    return dateKey(observed.getUTCFullYear(), observed.getUTCMonth() + 1, observed.getUTCDate());
  }
  return dateKey(year, month, day);
};

const nthWeekday = (year, month, weekday, occurrence) => {
  const date = utcDate(year, month, 1);
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() + 1);
  }
  date.setUTCDate(date.getUTCDate() + ((occurrence - 1) * 7));
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const lastWeekday = (year, month, weekday) => {
  const date = utcDate(year, month + 1, 0);
  while (date.getUTCDay() !== weekday) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = ((19 * a) + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
  const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
  const month = Math.floor((h + l - (7 * m) + 114) / 31);
  const day = ((h + l - (7 * m) + 114) % 31) + 1;
  return utcDate(year, month, day);
};

const goodFriday = (year) => {
  const date = easterSunday(year);
  date.setUTCDate(date.getUTCDate() - 2);
  return dateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const usMarketHolidayKeysForYear = (year) => {
  if (!usMarketHolidayCache.has(year)) {
    usMarketHolidayCache.set(
      year,
      new Set([
        observedNewYearsDay(year),
        nthWeekday(year, 1, 1, 3),
        nthWeekday(year, 2, 1, 3),
        goodFriday(year),
        lastWeekday(year, 5, 1),
        observedFixedHoliday(year, 6, 19),
        observedFixedHoliday(year, 7, 4),
        nthWeekday(year, 9, 1, 1),
        nthWeekday(year, 11, 4, 4),
        observedFixedHoliday(year, 12, 25),
      ].filter(Boolean)),
    );
  }
  return usMarketHolidayCache.get(year);
};

const usMarketEarlyCloseKeysForYear = (year) => {
  if (!usMarketEarlyCloseCache.has(year)) {
    const holidayKeys = usMarketHolidayKeysForYear(year);
    const keys = [
      dateKey(year, 7, 3),
      nthWeekday(year, 11, 5, 4),
      dateKey(year, 12, 24),
    ].filter((key) => {
      if (holidayKeys.has(key)) return false;
      const [rawYear, rawMonth, rawDay] = key.split("-").map(Number);
      return !isWeekendDate(rawYear, rawMonth, rawDay);
    });
    usMarketEarlyCloseCache.set(year, new Set(keys));
  }
  return usMarketEarlyCloseCache.get(year);
};

export const usEquitySessionForQuote = (rawDate = null) => {
  const absolute = parseAbsoluteDate(rawDate) || new Date();
  if (Number.isNaN(absolute.getTime())) return "unknown";

  const parts = usEquityPartsFormatter.formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const weekday = get("weekday");
  if (weekday === "Sat" || weekday === "Sun") return "weekend";

  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return "unknown";
  }

  const key = dateKey(year, month, day);
  if (usMarketHolidayKeysForYear(year).has(key)) return "holiday";

  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "unknown";

  const closeMinutes = usMarketEarlyCloseKeysForYear(year).has(key)
    ? US_EQUITY_EARLY_CLOSE_MINUTES
    : US_EQUITY_REGULAR_CLOSE_MINUTES;
  const minutes = hour * 60 + minute;
  if (
    minutes < US_EQUITY_PREMARKET_START_MINUTES ||
    minutes >= US_EQUITY_OVERNIGHT_START_MINUTES
  ) return "overnight";
  if (minutes < US_EQUITY_REGULAR_OPEN_MINUTES) return "premarket";
  if (minutes >= closeMinutes) return "afterhours";
  return "regular";
};

export const extendedHoursLabelForQuote = (rawDate = null) => {
  const session = usEquitySessionForQuote(rawDate);
  if (session === "overnight") return "Overnight";
  if (session === "premarket") return "Premarket";
  if (session === "holiday") return "Market closed";
  if (session === "afterhours" || session === "weekend") return "After hours";
  return "Extended hours";
};

export const supportsExtendedHoursQuote = (assetType = null) => {
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  return (
    !normalizedAssetType ||
    EXTENDED_HOURS_QUOTE_ASSET_TYPES.has(normalizedAssetType)
  );
};

export const pricePctChangeFieldForQuote = ({
  quoteAtIso = null,
  assetType = null,
} = {}) => {
  if (!supportsExtendedHoursQuote(assetType)) return "pricePctChange1d";
  if (!quoteAtIso) return "pricePctChange1d";

  const session = usEquitySessionForQuote(quoteAtIso);
  if (
    session === "overnight" ||
    session === "premarket" ||
    session === "afterhours" ||
    session === "weekend" ||
    session === "holiday"
  ) {
    return "extendedHoursPctChange";
  }

  return "pricePctChange1d";
};

const priceChangeFieldForPctField = (pctChangeField) =>
  pctChangeField === "extendedHoursPctChange"
    ? "extendedHoursPriceChange"
    : "priceChange1d";

const referenceFieldForPctField = (pctChangeField) =>
  pctChangeField === "extendedHoursPctChange"
    ? "extendedHoursReferencePrice"
    : "priceChangeReferencePrice";

const toQuoteNumber = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value).replace(/[%$,]/g, "").trim();
  if (!cleaned) return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const LIVE_PRICE_MARKER_ROLES = new Set(["live", "extended_hours", "as_of"]);
const REFERENCE_PRICE_MARKER_ROLES = new Set([
  "regular_close",
  "prior_close",
  "official_close",
  "prior_settlement",
  "reference_close",
]);

export const normalizeChartPriceMarkers = (markers, fallbackQuoteAtIso = null) =>
  (Array.isArray(markers) ? markers : [])
    .map((marker) => {
      if (!marker || typeof marker !== "object") return null;
      const role = String(marker.role || "").trim().toLowerCase();
      const price = toQuoteNumber(marker.price);
      if (!role || price == null) return null;

      return {
        role,
        price,
        label: marker.label || null,
        quoteAtIso:
          marker["event-at-utc"] || marker.eventAtUtc || fallbackQuoteAtIso || null,
      };
    })
    .filter(Boolean);

export const normalizedChartUpdatesForTicker = (websocketData, tickerId) => {
  const updatesByTicker = websocketData?.["chart-updates"];
  if (!updatesByTicker || typeof updatesByTicker !== "object") return [];

  const updates = updatesByTicker[tickerId] ?? updatesByTicker[String(tickerId)];
  return (Array.isArray(updates) ? updates : [])
    .filter((update) =>
      update &&
      typeof update === "object" &&
      update.version === "chart_update_v1",
    );
};

export const latestNormalizedChartUpdate = (updates) => {
  const rows = Array.isArray(updates) ? updates : [];
  if (rows.length === 0) return null;

  return rows.reduce((latest, update) => {
    if (!latest) return update;
    const latestAt = parseAbsoluteDate(
      latest["event-at-utc"] || latest.eventAtUtc,
    )?.getTime();
    const updateAt = parseAbsoluteDate(
      update["event-at-utc"] || update.eventAtUtc,
    )?.getTime();
    if (!Number.isFinite(updateAt)) return latest;
    if (!Number.isFinite(latestAt) || updateAt >= latestAt) return update;
    return latest;
  }, null);
};

export const chartUpdateBarWindowsForScale = ({ updates, scale } = {}) => {
  const normalizedScale = normalizeLiveChartScale(scale);
  const dailyLike = DAILY_LIKE_SCALES.has(normalizedScale);

  return (Array.isArray(updates) ? updates : [])
    .filter((update) => {
      if (String(update?.["event-kind"] || update?.eventKind) !== "bar") {
        return false;
      }
      const scalePermission = dailyLike
        ? update?.["daily-bar-update-allowed"] ?? update?.dailyBarUpdateAllowed
        : update?.["intraday-bar-update-allowed"] ??
          update?.intradayBarUpdateAllowed;
      const generalPermission =
        update?.["bar-update-allowed"] ?? update?.barUpdateAllowed;
      return scalePermission == null
        ? generalPermission === true
        : scalePermission === true;
    })
    .map((update) => update.bar)
    .filter((bar) => bar && typeof bar === "object");
};

export const buildChartQuotePresentation = ({
  displayData = {},
  assetType = null,
} = {}) => {
  if (!displayData || typeof displayData !== "object") {
    return { livePriceQuote: null, extendedHoursQuote: null, priceMarkers: [] };
  }

  const normalizedAssetType = String(
    displayData.assetType || assetType || "",
  ).trim().toLowerCase();
  const quoteAtIso =
    displayData.currentPriceUpdatedAtIso ||
    displayData.currentPriceUpdatedAt ||
    null;
  const explicitMarkers = normalizeChartPriceMarkers(
    displayData.priceMarkers || displayData["price-markers"],
    quoteAtIso,
  );
  const currentPrice = toQuoteNumber(
    displayData.currentPriceValue ?? displayData.currentPrice,
  );
  let priceMarkers = explicitMarkers;

  if (priceMarkers.length === 0 && currentPrice != null) {
    const session = usEquitySessionForQuote(quoteAtIso);
    const extendedSession = [
      "overnight",
      "premarket",
      "afterhours",
      "weekend",
      "holiday",
    ].includes(session);
    const liveRole =
      supportsExtendedHoursQuote(normalizedAssetType) && extendedSession
        ? "extended_hours"
        : "live";
    priceMarkers = [{ role: liveRole, price: currentPrice, quoteAtIso }];

    const closePrice = toQuoteNumber(
      displayData.closePriceValue ?? displayData.closePrice,
    );
    const moveReference = toQuoteNumber(
      displayData.extendedHoursReferencePrice ??
      displayData.priceChangeReferencePrice,
    );
    const referencePrice = firstFiniteNumber(closePrice, moveReference);
    if (referencePrice != null && liveRole === "extended_hours") {
      priceMarkers.push({ role: "regular_close", price: referencePrice });
    } else if (
      referencePrice != null &&
      ["futures"].includes(normalizedAssetType)
    ) {
      priceMarkers.push({ role: "prior_settlement", price: referencePrice });
    }
  }

  const liveMarker = priceMarkers.find((marker) =>
    LIVE_PRICE_MARKER_ROLES.has(marker.role),
  );
  const referenceMarker = priceMarkers.find((marker) =>
    REFERENCE_PRICE_MARKER_ROLES.has(marker.role),
  );
  const livePrice = liveMarker?.price ?? currentPrice;
  const livePriceQuote = livePrice == null
    ? null
    : {
        price: livePrice,
        quoteAtIso: liveMarker?.quoteAtIso || quoteAtIso,
        role: liveMarker?.role || "live",
      };
  const extendedHoursQuote =
    liveMarker?.role === "extended_hours" && referenceMarker?.role === "regular_close"
      ? {
          price: liveMarker.price,
          referencePrice: referenceMarker.price,
          label: liveMarker.label || extendedHoursLabelForQuote(quoteAtIso),
          quoteAtIso: liveMarker.quoteAtIso || quoteAtIso,
        }
      : null;

  return { livePriceQuote, extendedHoursQuote, priceMarkers };
};

const shouldUpdateQuoteMoveFields = ({ quoteAtIso = null, assetType = null } = {}) => {
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  if (normalizedAssetType !== "indices") return true;
  if (!quoteAtIso) return true;

  return usEquitySessionForQuote(quoteAtIso) === "regular";
};

export const buildLiveQuoteMoveUpdate = ({
  currentInfo = {},
  nextPrice,
  quoteAtIso = null,
  assetType = null,
  pctChangeValue = null,
  priceChangeValue = null,
  referencePriceValue = null,
  includeNullFields = false,
} = {}) => {
  const livePrice = toQuoteNumber(nextPrice);
  if (livePrice == null) return {};
  if (!shouldUpdateQuoteMoveFields({ quoteAtIso, assetType })) return {};

  const pctChangeField = pricePctChangeFieldForQuote({ quoteAtIso, assetType });
  const priceChangeField = priceChangeFieldForPctField(pctChangeField);
  const referenceField = referenceFieldForPctField(pctChangeField);

  const payloadPriceChange = toQuoteNumber(priceChangeValue);
  const payloadPctChange = toQuoteNumber(pctChangeValue);
  const currentPrice = toQuoteNumber(
    currentInfo?.currentPriceValue ?? currentInfo?.currentPrice,
  );
  const currentPriceChange = toQuoteNumber(currentInfo?.[priceChangeField]);
  const currentPctChange = toQuoteNumber(currentInfo?.[pctChangeField]);
  const currentReference = toQuoteNumber(currentInfo?.[referenceField]);
  const extendedHoursCloseReference =
    pctChangeField === "extendedHoursPctChange"
      ? toQuoteNumber(currentInfo?.closePriceValue ?? currentInfo?.closePrice)
      : null;

  const referenceFromPayloadPriceChange =
    payloadPriceChange != null ? livePrice - payloadPriceChange : null;
  const referenceFromPayloadPct =
    payloadPctChange != null && payloadPctChange !== -100
      ? livePrice / (1 + payloadPctChange / 100)
      : null;
  const referenceFromCurrentPriceChange =
    currentPrice != null && currentPriceChange != null
      ? currentPrice - currentPriceChange
      : null;
  const referenceFromCurrentPct =
    currentPrice != null && currentPctChange != null && currentPctChange !== -100
      ? currentPrice / (1 + currentPctChange / 100)
      : null;
  const referencePrice = firstFiniteNumber(
    toQuoteNumber(referencePriceValue),
    referenceFromPayloadPriceChange,
    currentReference,
    extendedHoursCloseReference,
    referenceFromCurrentPriceChange,
    referenceFromPayloadPct,
    referenceFromCurrentPct,
  );

  const nextPriceChange =
    payloadPriceChange != null
      ? payloadPriceChange
      : referencePrice != null
        ? livePrice - referencePrice
        : null;
  const nextPctChange =
    payloadPctChange != null
      ? payloadPctChange
      : referencePrice != null && referencePrice !== 0
        ? ((livePrice / referencePrice) - 1) * 100
        : null;

  const fields = {};
  if (nextPriceChange != null || includeNullFields) {
    fields[priceChangeField] = nextPriceChange;
  }
  if (nextPctChange != null || includeNullFields) {
    fields[pctChangeField] = nextPctChange;
  }
  if (referencePrice != null) fields[referenceField] = referencePrice;
  return fields;
};

const shouldMergeQuoteIntoScale = ({
  scale,
  assetType,
  quoteAtIso,
  sessionPermissionOverride = null,
}) => {
  if (typeof sessionPermissionOverride === "boolean") {
    return sessionPermissionOverride;
  }
  const normalizedScale = normalizeLiveChartScale(scale);
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  const equityLike =
    !normalizedAssetType || US_EQUITY_LIKE_ASSET_TYPES.has(normalizedAssetType);
  if (!equityLike) return true;

  const session = usEquitySessionForQuote(quoteAtIso);
  if (DAILY_LIKE_SCALES.has(normalizedScale)) return session === "regular";

  return ["premarket", "regular", "afterhours"].includes(session);
};

const bucketStartFromDate = (date, rawScale) => {
  if (!date) return null;

  const scale = normalizeLiveChartScale(rawScale);
  const bucketSeconds = SCALE_TO_SECONDS[scale];
  if (bucketSeconds) {
    return new Date(
      Math.floor(date.getTime() / (bucketSeconds * 1000)) * bucketSeconds * 1000,
    );
  }
  const bucketMinutes = SCALE_TO_MINUTES[scale];
  if (bucketMinutes) {
    const minutes = date.getUTCMinutes();
    const bucketMinute = Math.floor(minutes / bucketMinutes) * bucketMinutes;
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        bucketMinute,
        0,
        0,
      ),
    );
  }

  if (scale === "1d") {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
    );
  }

  if (scale === "1w") {
    const weekday = date.getUTCDay();
    const diff = weekday === 0 ? -6 : 1 - weekday;
    return new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate() + diff,
        0,
        0,
        0,
        0
      )
    );
  }

  if (scale === "1mo") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));
  }

  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  );
};

const bucketStartUtc = (rawDate, rawScale) => {
  const date = parseUtcDate(rawDate);
  return bucketStartFromDate(date, rawScale);
};

const addBucketStep = (date, rawScale) => {
  const scale = normalizeLiveChartScale(rawScale);
  const bucketSeconds = SCALE_TO_SECONDS[scale];
  if (bucketSeconds) {
    return new Date(date.getTime() + bucketSeconds * 1000);
  }
  const bucketMinutes = SCALE_TO_MINUTES[scale];
  if (bucketMinutes) {
    return new Date(date.getTime() + bucketMinutes * 60 * 1000);
  }
  if (scale === "1d") return new Date(date.getTime() + 24 * 60 * 60 * 1000);
  if (scale === "1w") return new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000);
  if (scale === "1mo") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1, 0, 0, 0, 0));
  }
  return new Date(date.getTime() + 24 * 60 * 60 * 1000);
};

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const resolveQuoteBucketDate = ({ quoteAtIso, scale, lastBarDatetime }) => {
  const normalizedBucket = bucketStartUtc(quoteAtIso, scale);
  const absoluteBucket = bucketStartFromDate(parseAbsoluteDate(quoteAtIso), scale);

  if (normalizedBucket && hasExplicitTimeZone(quoteAtIso)) {
    return normalizedBucket;
  }

  if (!lastBarDatetime) {
    return normalizedBucket || absoluteBucket;
  }

  const lastBucketDate = bucketStartUtc(lastBarDatetime, scale);
  if (!lastBucketDate) {
    return normalizedBucket || absoluteBucket;
  }

  const candidates = [normalizedBucket, absoluteBucket]
    .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
    .map((date) => ({
      date,
      diffMs: date.getTime() - lastBucketDate.getTime(),
    }));

  if (candidates.length === 0) return null;

  const nextForward = candidates
    .filter((candidate) => candidate.diffMs >= 0)
    .sort((a, b) => a.diffMs - b.diffMs)[0];

  if (nextForward) {
    return nextForward.date;
  }

  return candidates.sort(
    (a, b) => Math.abs(a.diffMs) - Math.abs(b.diffMs)
  )[0].date;
};

export const mergeQuoteIntoSeries = ({
  series,
  scale,
  price,
  quoteAtIso,
  tradingDate = null,
  assetType = null,
  sessionPermissionOverride = null,
}) => {
  if (!Array.isArray(series) || series.length === 0) return series;

  const livePrice = toFiniteNumber(price);
  if (livePrice == null) return series;
  if (!shouldMergeQuoteIntoScale({
    scale,
    assetType,
    quoteAtIso,
    sessionPermissionOverride,
  })) return series;

  const normalizedScale = normalizeLiveChartScale(scale);
  const normalizedTradingDate = /^\d{4}-\d{2}-\d{2}$/.test(
    String(tradingDate || "").trim(),
  )
    ? String(tradingDate).trim()
    : null;
  const bucketIdentitySource =
    DAILY_LIKE_SCALES.has(normalizedScale) && normalizedTradingDate
      ? `${normalizedTradingDate} 00:00:00`
      : quoteAtIso;
  const normalizedQuoteBucket = bucketStartUtc(bucketIdentitySource, scale);
  const maxExpectedSeriesBucket =
    (SCALE_TO_SECONDS[normalizedScale] || SCALE_TO_MINUTES[normalizedScale]) &&
      normalizedQuoteBucket
      ? addBucketStep(normalizedQuoteBucket, scale)
      : null;
  const workingSeries = maxExpectedSeriesBucket
    ? series.filter((entry) => {
        const entryBucket = bucketStartUtc(entry?.datetime, scale);
        return !entryBucket || entryBucket <= maxExpectedSeriesBucket;
      })
    : series;
  if (workingSeries.length === 0) return series;
  const lastBar = workingSeries[workingSeries.length - 1];
  const quoteAtMillis = parseAbsoluteDate(quoteAtIso)?.getTime() || null;
  const bucketDate =
    DAILY_LIKE_SCALES.has(normalizedScale) && normalizedTradingDate
      ? normalizedQuoteBucket
      : resolveQuoteBucketDate({
          quoteAtIso: bucketIdentitySource,
          scale,
          lastBarDatetime: lastBar?.datetime,
        });
  if (!bucketDate) return workingSeries;

  const lastBucketDate = bucketStartUtc(lastBar?.datetime, scale);
  if (!lastBucketDate) return workingSeries;
  const nowBucketDate = bucketStartUtc(new Date().toISOString(), scale);
  const maxAllowedBucketDate = nowBucketDate
    ? addBucketStep(nowBucketDate, scale)
    : null;

  if (
    maxAllowedBucketDate &&
    bucketDate.getTime() > maxAllowedBucketDate.getTime()
  ) {
    return workingSeries;
  }

  if (bucketDate.getTime() < lastBucketDate.getTime()) {
    return workingSeries;
  }

  if (bucketDate.getTime() === lastBucketDate.getTime()) {
    const existingCloseAt = Number(lastBar?._liveCloseAt);
    if (
      Number.isFinite(existingCloseAt) &&
      Number.isFinite(quoteAtMillis) &&
      quoteAtMillis < existingCloseAt
    ) {
      return workingSeries;
    }
    const nextLast = {
      ...lastBar,
      open: toFiniteNumber(lastBar.open) ?? livePrice,
      high: Math.max(toFiniteNumber(lastBar.high) ?? livePrice, livePrice),
      low: Math.min(toFiniteNumber(lastBar.low) ?? livePrice, livePrice),
      close: livePrice,
      _liveCloseAt: quoteAtMillis ?? existingCloseAt,
    };
    return workingSeries.slice(0, -1).concat(nextLast);
  }

  const targetDatetime = formatChartDatetime(bucketDate, bucketIdentitySource);

  const previousClose =
    toFiniteNumber(lastBar.close) ??
    toFiniteNumber(lastBar.open) ??
    livePrice;
  const nextOpen = SCALE_TO_SECONDS[normalizedScale] ? livePrice : previousClose;

  return workingSeries.concat({
    datetime: targetDatetime,
    open: nextOpen,
    high: Math.max(nextOpen, livePrice),
    low: Math.min(nextOpen, livePrice),
    close: livePrice,
    volume: 0,
    vwap:
      lastBar && lastBar.vwap != null && Number.isFinite(Number(lastBar.vwap))
        ? Number(lastBar.vwap)
        : null,
    _liveCloseAt: quoteAtMillis,
  });
};

export const mergeQuoteIntoSeriesMap = ({
  seriesMap,
  tickerId,
  idx,
  price,
  quoteAtIso,
  assetType = null,
}) => {
  if (!seriesMap || typeof seriesMap !== "object") return seriesMap;
  if (tickerId == null || idx == null) return seriesMap;

  const prefix = `${String(tickerId)}-`;
  const suffix = `-${String(idx)}`;
  let changed = false;
  const nextMap = { ...seriesMap };

  Object.keys(seriesMap).forEach((key) => {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) return;
    const scale = key.slice(prefix.length, key.length - suffix.length);
    const currentSeries = seriesMap[key];
    const nextSeries = mergeQuoteIntoSeries({
      series: currentSeries,
      scale,
      price,
      quoteAtIso,
      assetType,
    });

    if (nextSeries !== currentSeries) {
      nextMap[key] = nextSeries;
      changed = true;
    }
  });

  return changed ? nextMap : seriesMap;
};

const normalizedBarWindow = (window, scale, assetType = null) => {
  if (!window || typeof window !== "object") return null;

  const normalizedScale = normalizeLiveChartScale(scale);
  const normalizedAssetType = String(assetType || "").trim().toLowerCase();
  // The evening reopen still falls on today's Pacific calendar date, but its
  // daily candle belongs to the provider's next exchange-session date.
  const futuresSessionDate = window["session-date"] || window.sessionDate || null;
  const futuresSessionDatetime = /^\d{4}-\d{2}-\d{2}$/.test(
    String(futuresSessionDate || "").trim(),
  )
    ? `${String(futuresSessionDate).trim()} 00:00:00`
    : futuresSessionDate;
  const sourceDate =
    normalizedAssetType === "futures" &&
    DAILY_LIKE_SCALES.has(normalizedScale) &&
    futuresSessionDatetime
      ? futuresSessionDatetime
      : window["start-at-iso"] || window.startAtIso || window.datetime || null;
  const bucketDate = bucketStartUtc(sourceDate, scale);
  if (!bucketDate) return null;

  const open = toFiniteNumber(window.open);
  const high = toFiniteNumber(window.high);
  const low = toFiniteNumber(window.low);
  const close = toFiniteNumber(window.close);
  if ([open, high, low, close].some((value) => value == null)) return null;

  const lastAt =
    parseAbsoluteDate(window["last-at-iso"] || window.lastAtIso || sourceDate)
      ?.getTime() || null;
  const volumeDelta = toFiniteNumber(
    window["volume-delta"] ?? window.volumeDelta,
  );

  return {
    datetime: formatChartDatetime(bucketDate, sourceDate),
    bucketDate,
    open,
    high: Math.max(open, high, low, close),
    low: Math.min(open, high, low, close),
    close,
    volumeDelta:
      volumeDelta != null && volumeDelta >= 0 ? volumeDelta : null,
    lastAt,
  };
};

export const barWindowsSupportScale = ({ scale, windows }) => {
  const scaleSeconds = SCALE_TO_SECONDS[normalizeLiveChartScale(scale)];
  if (!scaleSeconds) return true;
  if (!Array.isArray(windows) || windows.length === 0) return false;

  return windows.every((window) => {
    const durationSeconds = toFiniteNumber(
      window?.["duration-seconds"] ?? window?.durationSeconds,
    );
    return durationSeconds != null &&
      durationSeconds > 0 &&
      durationSeconds <= scaleSeconds;
  });
};

export const mergeBarWindowsIntoSeries = ({
  series,
  scale,
  windows,
  assetType = null,
  sessionPolicyAuthoritative = false,
}) => {
  if (!Array.isArray(series) || series.length === 0) return series;
  if (!Array.isArray(windows) || windows.length === 0) return series;

  const normalizedWindows = windows
    .filter((window) =>
      shouldMergeQuoteIntoScale({
        scale,
        assetType,
        quoteAtIso: window?.["last-at-iso"] || window?.lastAtIso,
        sessionPermissionOverride: sessionPolicyAuthoritative ? true : null,
      })
    )
    .map((window) => normalizedBarWindow(window, scale, assetType))
    .filter(Boolean)
    .sort((left, right) => left.bucketDate - right.bucketDate);
  if (normalizedWindows.length === 0) return series;

  const normalizedScale = normalizeLiveChartScale(scale);
  const latestWindowBucket =
    normalizedWindows[normalizedWindows.length - 1]?.bucketDate || null;
  const maxExpectedSeriesBucket =
    (SCALE_TO_SECONDS[normalizedScale] || SCALE_TO_MINUTES[normalizedScale]) &&
      latestWindowBucket
      ? addBucketStep(latestWindowBucket, scale)
      : null;
  const filteredSeries = maxExpectedSeriesBucket
    ? series.filter((entry) => {
        const entryBucket = bucketStartUtc(entry?.datetime, scale);
        return !entryBucket || entryBucket <= maxExpectedSeriesBucket;
      })
    : series;
  let nextSeries =
    filteredSeries.length === series.length ? series : filteredSeries;
  normalizedWindows.forEach((window) => {
    const lastIndex = nextSeries.length - 1;
    const lastBar = nextSeries[lastIndex];
    const lastBucket = bucketStartUtc(lastBar?.datetime, scale);
    let matchingIndex =
      lastBucket && lastBucket.getTime() === window.bucketDate.getTime()
        ? lastIndex
        : -1;
    if (
      matchingIndex < 0 &&
      lastBucket &&
      window.bucketDate < lastBucket
    ) {
      matchingIndex = nextSeries.findIndex((entry) => {
        const entryBucket = bucketStartUtc(entry?.datetime, scale);
        return (
          entryBucket && entryBucket.getTime() === window.bucketDate.getTime()
        );
      });
    }

    if (matchingIndex >= 0) {
      const existing = nextSeries[matchingIndex];
      const existingCloseAt = Number(existing?._liveCloseAt);
      const shouldUpdateClose =
        !Number.isFinite(existingCloseAt) ||
        !Number.isFinite(window.lastAt) ||
        window.lastAt >= existingCloseAt;
      const updated = {
        ...existing,
        open: toFiniteNumber(existing.open) ?? window.open,
        high: Math.max(toFiniteNumber(existing.high) ?? window.high, window.high),
        low: Math.min(toFiniteNumber(existing.low) ?? window.low, window.low),
        close: shouldUpdateClose
          ? window.close
          : toFiniteNumber(existing.close) ?? window.close,
        volume:
          window.volumeDelta == null
            ? existing.volume
            : (toFiniteNumber(existing.volume) ?? 0) + window.volumeDelta,
        _liveCloseAt: shouldUpdateClose ? window.lastAt : existingCloseAt,
      };
      nextSeries =
        nextSeries.slice(0, matchingIndex)
          .concat(updated, nextSeries.slice(matchingIndex + 1));
      return;
    }

    const activeLastBar = nextSeries[nextSeries.length - 1];
    const activeLastBucket = bucketStartUtc(activeLastBar?.datetime, scale);
    if (activeLastBucket && window.bucketDate <= activeLastBucket) return;

    nextSeries = nextSeries.concat({
      datetime: window.datetime,
      open: window.open,
      high: window.high,
      low: window.low,
      close: window.close,
      volume: window.volumeDelta ?? 0,
      vwap:
        activeLastBar &&
        activeLastBar.vwap != null &&
        Number.isFinite(Number(activeLastBar.vwap))
          ? Number(activeLastBar.vwap)
          : null,
      _liveCloseAt: window.lastAt,
    });
  });

  return nextSeries;
};

export const mergeBarWindowsIntoSeriesMap = ({
  seriesMap,
  tickerId,
  idx,
  windows,
  assetType = null,
}) => {
  if (!seriesMap || typeof seriesMap !== "object") return seriesMap;
  if (tickerId == null || idx == null) return seriesMap;

  const prefix = `${String(tickerId)}-`;
  const suffix = `-${String(idx)}`;
  let changed = false;
  const nextMap = { ...seriesMap };

  Object.keys(seriesMap).forEach((key) => {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) return;

    const scale = key.slice(prefix.length, key.length - suffix.length);
    const currentSeries = seriesMap[key];
    const nextSeries = mergeBarWindowsIntoSeries({
      series: currentSeries,
      scale,
      windows,
      assetType,
    });
    if (nextSeries !== currentSeries) {
      nextMap[key] = nextSeries;
      changed = true;
    }
  });

  return changed ? nextMap : seriesMap;
};

export const mergeChartUpdatesIntoSeriesMap = ({
  seriesMap,
  tickerId,
  idx,
  updates,
  assetType = null,
}) => {
  if (!seriesMap || typeof seriesMap !== "object") return seriesMap;
  if (tickerId == null || idx == null) return seriesMap;

  const prefix = `${String(tickerId)}-`;
  const suffix = `-${String(idx)}`;
  let changed = false;
  const nextMap = { ...seriesMap };

  Object.keys(seriesMap).forEach((key) => {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) return;

    const scale = key.slice(prefix.length, key.length - suffix.length);
    const windows = chartUpdateBarWindowsForScale({ updates, scale });
    const currentSeries = seriesMap[key];
    let nextSeries = currentSeries;
    if (
      windows.length > 0 &&
      barWindowsSupportScale({ scale, windows })
    ) {
      nextSeries = mergeBarWindowsIntoSeries({
        series: currentSeries,
        scale,
        windows,
        assetType,
        sessionPolicyAuthoritative: true,
      });
    } else {
      const normalizedScale = normalizeLiveChartScale(scale);
      const dailyLike = DAILY_LIKE_SCALES.has(normalizedScale);
      const quoteUpdate = [...(Array.isArray(updates) ? updates : [])]
        .reverse()
        .find((update) => {
          if (String(update?.["event-kind"] || update?.eventKind) !== "quote") {
            return false;
          }
          if (
            (update?.["quote-can-update-bar"] ?? update?.quoteCanUpdateBar) !==
            true
          ) {
            return false;
          }
          const permitted = dailyLike
            ? update?.["daily-bar-update-allowed"] ?? update?.dailyBarUpdateAllowed
            : update?.["intraday-bar-update-allowed"] ??
              update?.intradayBarUpdateAllowed;
          return permitted === true;
        });
      if (quoteUpdate) {
        nextSeries = mergeQuoteIntoSeries({
          series: currentSeries,
          scale,
          price: quoteUpdate.price,
          quoteAtIso:
            quoteUpdate["event-at-utc"] || quoteUpdate.eventAtUtc,
          tradingDate:
            quoteUpdate["trading-date"] || quoteUpdate.tradingDate,
          assetType,
          sessionPermissionOverride: true,
        });
      }
    }
    if (nextSeries !== currentSeries) {
      nextMap[key] = nextSeries;
      changed = true;
    }
  });

  return changed ? nextMap : seriesMap;
};

const normalizedBarCorrection = (correction, scale) => {
  if (!correction || typeof correction !== "object") return null;
  if (
    normalizeLiveChartScale(correction.interval) !==
    normalizeLiveChartScale(scale)
  ) {
    return null;
  }

  const bucketDate = bucketStartUtc(correction.datetime, scale);
  const revision = Number(correction.revision);
  const open = toFiniteNumber(correction.open);
  const high = toFiniteNumber(correction.high);
  const low = toFiniteNumber(correction.low);
  const close = toFiniteNumber(correction.close);
  const volume = toFiniteNumber(correction.volume);
  if (!bucketDate || !Number.isInteger(revision) || revision <= 0) return null;
  if ([open, high, low, close, volume].some((value) => value == null)) return null;
  if (volume < 0 || high < Math.max(open, low, close) || low > Math.min(open, high, close)) {
    return null;
  }

  const vwap =
    correction.vwap == null ? null : toFiniteNumber(correction.vwap);
  if (correction.vwap != null && vwap == null) return null;

  return {
    datetime: formatChartDatetime(bucketDate, correction.datetime),
    bucketDate,
    open,
    high,
    low,
    close,
    volume,
    vwap,
    revision,
  };
};

export const replaceBarCorrectionsInSeries = ({
  series,
  scale,
  corrections,
}) => {
  if (!Array.isArray(series) || series.length === 0) return series;
  if (!Array.isArray(corrections) || corrections.length === 0) return series;

  const normalized = corrections
    .map((correction) => normalizedBarCorrection(correction, scale))
    .filter(Boolean);
  if (normalized.length === 0) return series;

  let nextSeries = series;
  normalized.forEach((correction) => {
    const matchingIndex = nextSeries.findIndex((entry) => {
      const bucket = bucketStartUtc(entry?.datetime, scale);
      return bucket && bucket.getTime() === correction.bucketDate.getTime();
    });
    if (matchingIndex < 0) {
      const firstBucket = bucketStartUtc(nextSeries[0]?.datetime, scale);
      if (firstBucket && correction.bucketDate < firstBucket) return;

      const inserted = {
        datetime: correction.datetime,
        open: correction.open,
        high: correction.high,
        low: correction.low,
        close: correction.close,
        volume: correction.volume,
        vwap: correction.vwap,
        _correctionRevision: correction.revision,
      };
      const insertAt = nextSeries.findIndex((entry) => {
        const bucket = bucketStartUtc(entry?.datetime, scale);
        return bucket && bucket > correction.bucketDate;
      });
      nextSeries = insertAt < 0
        ? nextSeries.concat(inserted)
        : nextSeries
          .slice(0, insertAt)
          .concat(inserted, nextSeries.slice(insertAt));
      return;
    }

    const existing = nextSeries[matchingIndex];
    const existingRevision = Number(existing?._correctionRevision) || 0;
    if (correction.revision <= existingRevision) return;

    const replacement = {
      ...existing,
      datetime: correction.datetime,
      open: correction.open,
      high: correction.high,
      low: correction.low,
      close: correction.close,
      volume: correction.volume,
      vwap: correction.vwap,
      _correctionRevision: correction.revision,
    };
    nextSeries = nextSeries
      .slice(0, matchingIndex)
      .concat(replacement, nextSeries.slice(matchingIndex + 1));
  });

  return nextSeries;
};

export const replaceBarCorrectionsInSeriesMap = ({
  seriesMap,
  tickerId,
  idx,
  corrections,
}) => {
  if (!seriesMap || typeof seriesMap !== "object") return seriesMap;
  if (tickerId == null || idx == null) return seriesMap;

  const prefix = `${String(tickerId)}-`;
  const suffix = `-${String(idx)}`;
  let changed = false;
  const nextMap = { ...seriesMap };

  Object.keys(seriesMap).forEach((key) => {
    if (!key.startsWith(prefix) || !key.endsWith(suffix)) return;

    const scale = key.slice(prefix.length, key.length - suffix.length);
    const currentSeries = seriesMap[key];
    const nextSeries = replaceBarCorrectionsInSeries({
      series: currentSeries,
      scale,
      corrections,
    });
    if (nextSeries !== currentSeries) {
      nextMap[key] = nextSeries;
      changed = true;
    }
  });

  return changed ? nextMap : seriesMap;
};
